import { v4 as uuidv4 } from 'uuid';

import type { EventSchema, SponsorInfoSchema } from '~/types/schema';
import { EventAssociatedModel, EventSponsorInfo } from '~/types/adamTypes';

export type UseAppEventSponsorInfo = {
  openMiniApp: (data: SponsorInfoSchema | EventSchema) => Promise<void>;
  closeMiniApp: (data: SponsorInfoSchema | EventSchema) => Promise<void>;
  websiteViewed: (data: SponsorInfoSchema | EventSchema) => Promise<void>;
  eventsViewed: (data: SponsorInfoSchema | EventSchema) => Promise<void>;
  schoolBasketSponsorInfoViewed: (
    data: SponsorInfoSchema | EventSchema
  ) => Promise<void>;
  categoryViewed: (
    data: SponsorInfoSchema | EventSchema,
    contentId: string
  ) => Promise<void>;
  videoViewed: (
    data: SponsorInfoSchema | EventSchema,
    videoUrl: string
  ) => Promise<void>;
  contactAppClicked: (
    data: SponsorInfoSchema | EventSchema,
    source: SponsorInfoEngagementSources
  ) => Promise<void>;
  monAvenirSponsorInfoViewed: (
    data: SponsorInfoSchema | EventSchema,
    sectionId: string
  ) => Promise<void>;
  boxClicked: (
    sponsorBoxId: string,
    sponsorBoxSectionId: string
  ) => Promise<void>;
  shareMiniApp: (data: SponsorInfoSchema | EventSchema) => Promise<void>;
  categoryCustomerClicked: (
    categoryId: string,
    customerWebLink?: string,
    sponsorId?: string
  ) => Promise<void>;
};

export const INJECT_APP_EVENT_SPONSOR_INFO = Symbol(
  'useAppEventSponsorInfo composable'
) as InjectionKey<UseAppEventSponsorInfo>;

export const useAppEventSponsorInfo = (): UseAppEventSponsorInfo => {
  const sessionOrientation = useNomadStorage('orienation', uuidv4());

  const { createEvent, setContentId } = useAppEvent(
    EventAssociatedModel.SponsorInfo
  );

  const getEventData = () => {
    const eventData: any = {};

    return eventData;
  };

  const getSponsorId = (data: SponsorInfoSchema | EventSchema) => {
    const sponsorId =
      data.sponsorsArray?.length && data.sponsorsArray.length >= 1
        ? data.sponsorsArray[0].id
        : '';
    return sponsorId;
  };

  const openMiniApp = async (data: SponsorInfoSchema | EventSchema) => {
    const eventData = getEventData();

    setContentId(getSponsorId(data));
    eventData.VisitSessionId = sessionOrientation.value;
    await createEvent(EventSponsorInfo.MainPageViewed, eventData);
  };

  const closeMiniApp = async (data: SponsorInfoSchema | EventSchema) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    eventData.VisitSessionId = sessionOrientation.value;
    eventData.date = new Date().toISOString();
    await createEvent(EventSponsorInfo.EndVisit, eventData);
  };

  const websiteViewed = async (data: SponsorInfoSchema | EventSchema) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    await createEvent(EventSponsorInfo.WebsiteViewed, eventData);
  };

  const eventsViewed = async (data: SponsorInfoSchema | EventSchema) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    await createEvent(EventSponsorInfo.EventsViewed, eventData);
  };

  const categoryViewed = async (
    data: SponsorInfoSchema | EventSchema,
    contentId: string
  ) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    eventData.SectionId = contentId;
    await createEvent(EventSponsorInfo.CategoryViewed, eventData);
  };

  const videoViewed = async (
    data: SponsorInfoSchema | EventSchema,
    videoUrl: string
  ) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    eventData.VideoLink = videoUrl;
    await createEvent(EventSponsorInfo.VideoViewed, eventData);
  };

  const contactAppClicked = async (
    data: SponsorInfoSchema | EventSchema,
    source: SponsorInfoEngagementSources
  ) => {
    let eventType: string;
    switch (source) {
      case SponsorInfoEngagementSources.WhatsApp:
        eventType = EventSponsorInfo.RequestWhatsAppClicked;
        break;
      case SponsorInfoEngagementSources.Phone:
        eventType = EventSponsorInfo.RequestCalledClicked;
        break;
      case SponsorInfoEngagementSources.Brochure:
        eventType = EventSponsorInfo.RequestBrochureClicked;
        break;
    }
    if (!eventType) return;
    const eventData = getEventData();
    setContentId(getSponsorId(data));

    await createEvent(eventType, eventData);
  };

  const schoolBasketSponsorInfoViewed = async (
    data: SponsorInfoSchema | EventSchema
  ) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    await createEvent(
      EventSponsorInfo.SchoolBasketSponsorInfoViewed,
      eventData
    );
  };

  const monAvenirSponsorInfoViewed = async (
    data: SponsorInfoSchema | EventSchema,
    sectionId: string
  ) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    eventData.SectionMonAvenir = sectionId;
    await createEvent(EventSponsorInfo.MonAvenirSponsorInfoViewed, eventData);
  };

  const boxClicked = async (
    sponsorBoxId: string,
    sponsorBoxSectionId: string
  ) => {
    const eventData = getEventData();

    setContentId(sponsorBoxId);

    eventData.SectionMonAvenir = sponsorBoxSectionId;

    await createEvent(EventSponsorInfo.MonAvenirSponsorInfoViewed, eventData);
  };

  const categoryCustomerClicked = async (
    categoryId: string,
    customerWebLink?: string,
    sponsorId?: string
  ) => {
    const eventData = getEventData();
    eventData.CategoryId = categoryId;
    if (customerWebLink) eventData.Url = customerWebLink;
    if (sponsorId) eventData.SponsorId = sponsorId;

    await createEvent(EventSponsorInfo.CategoryCustomerClicked, eventData);
  };

  const shareMiniApp = async (data: SponsorInfoSchema | EventSchema) => {
    const eventData = getEventData();
    setContentId(getSponsorId(data));
    await createEvent(EventSponsorInfo.ShareClicked, eventData);
  };

  return {
    openMiniApp,
    closeMiniApp,
    websiteViewed,
    eventsViewed,
    categoryViewed,
    videoViewed,
    contactAppClicked,
    schoolBasketSponsorInfoViewed,
    monAvenirSponsorInfoViewed,
    boxClicked,
    shareMiniApp,
    categoryCustomerClicked,
  };
};
